/**
 * daily.ts — daily-login bonus. The wall-clock "now" is passed in from the UI
 * (the one legitimate place a real calendar day matters); systems never read
 * Date.now() directly.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { addModifier } from './modifiers';
import { format } from '../core/numbers';

const DAY_MS = 86_400_000;

export function dayIndex(wallNowMs: number): number {
  return Math.floor(wallNowMs / DAY_MS);
}

export function canClaimDaily(state: GameState, wallNowMs: number): boolean {
  return dayIndex(wallNowMs) > state.daily.lastClaimDay;
}

export interface DailyResult {
  reward: number;
  streak: number;
}

/** Claim the daily bonus: scaled cash + a short global boost. Returns null if already claimed. */
export function claimDaily(state: GameState, wallNowMs: number, bus?: EventBus): DailyResult | null {
  if (!canClaimDaily(state, wallNowMs)) return null;
  const day = dayIndex(wallNowMs);
  const streak = day === state.daily.lastClaimDay + 1 ? state.daily.streak + 1 : 1;
  const cappedStreak = Math.min(streak, 7);

  const reward = Math.max(200, state.stats.lifetimeEarnings * 0.05) * cappedStreak;
  state.economy.money += reward;
  state.stats.lifetimeEarnings += reward;
  state.daily.lastClaimDay = day;
  state.daily.streak = streak;

  // A short global boost as a cherry on top.
  addModifier(state, {
    id: 'daily:boost',
    label: 'Täglicher Boost',
    effect: { type: 'globalMult', value: 2 },
    endsAt: state.meta.gameTimeMs + 120_000,
  });

  bus?.emit('notify', { kind: 'reward', message: `🎁 Täglicher Bonus: +$${format(reward)} (Serie ${streak})` });
  return { reward, streak };
}
